import { Button, DraggableWindowHeader, TextInput } from "@hoylu/client-common";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isPasswordRequired as selectIsPasswordRequired } from "../../state/user/user.selector";
import { selectDashboardSelectedProject } from "../../state/workspaces/projects.selector";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import { getWaitingToCreateFromTemplate } from "../../state/workspaces/workspaces.selector";
import {
  getCreateBlankWorkspacePayload,
  getCreateFromTemplatePayload,
} from "../../utils/create.workspace.utils";
import { useI18n } from "../../utils/hooks/use.i18n";
import { useTextInputLocalization } from "../../utils/useTextInputLocalization";
import { Modal } from "./Modal";
import Styles from "./new.workspace.passwords.dialog.module.css";

export const NewWorkspacePasswordsDialog = () => {
  const dispatch = useDispatch();
  const t = useI18n("NEW_WORKSPACE_DIALOG.");
  const tClose = useI18n("CLOSE_BUTTON.");

  const localizationDict = useTextInputLocalization();

  const templateDetails = useSelector(getWaitingToCreateFromTemplate);
  const hasToSetPassword = useSelector(selectIsPasswordRequired);
  const selectedProjectId = useSelector(selectDashboardSelectedProject)?.id;

  const [templatePassword, setTemplatePassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const cancel = () => {
    dispatch(workspacesActions.cancelDashboardOption());
  };

  const handleCreationFromTemplate = () => {
    if (templateDetails) {
      const payload = getCreateFromTemplatePayload(
        templateDetails.workspaceId,
        selectedProjectId,
      );

      if (templateDetails.hasPassword) {
        payload.password = templatePassword;
      }

      if (hasToSetPassword) {
        payload.newPassword = newPassword;
      }

      dispatch(workspacesActions.duplicateWorkspace.request({ ...payload }));
    }
  };

  const handleBlankWorkspaceCreation = () => {
    const payload = getCreateBlankWorkspacePayload();

    if (hasToSetPassword) {
      payload.password = newPassword;
    }

    dispatch(workspacesActions.createWorkspace.request(payload));
  };

  const sendRequest = () => {
    if (templateDetails) {
      handleCreationFromTemplate();
    } else {
      handleBlankWorkspaceCreation();
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (shouldDisable()) return;

    sendRequest();
  };

  const shouldDisable = () => {
    const isBlockedByTemplatePassword =
      templateDetails?.hasPassword && !templatePassword;
    const isBlockedByNewPassword = hasToSetPassword && newPassword.length < 8;

    return isBlockedByTemplatePassword || isBlockedByNewPassword;
  };

  return (
    <Modal handleOutsideClick={cancel}>
      <DraggableWindowHeader
        close={tClose("CLOSE")}
        onClose={cancel}
        title={t("CREATE_WORKSPACE")}
      />
      <form className={Styles.form} onSubmit={handleSubmit}>
        {templateDetails?.hasPassword && (
          <TextInput
            isFocus
            label={t("PASSWORD")}
            localizationDict={localizationDict}
            onChange={setTemplatePassword}
            toolTip={t("PASSWORD")}
            type="password"
            useTogglePassword
            value={templatePassword}
          />
        )}
        {hasToSetPassword && (
          <TextInput
            isFocus={!templateDetails?.hasPassword}
            label={`${t("NEW_PASSWORD")} - ${t("CHARACTERS")}`}
            localizationDict={localizationDict}
            onChange={setNewPassword}
            type="password"
            useTogglePassword
            value={newPassword}
          />
        )}
        <div className={Styles.buttons}>
          <Button
            disabled={shouldDisable()}
            onClick={sendRequest}
            toolTip={t("CONTINUE")}
            type="primary"
          >
            {t("CONTINUE")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
